import React, { useEffect, useRef, useState } from 'react';
import { Hand, Mic, MicOff, PhoneOff, Send } from 'lucide-react';
import useVoiceSession from '../hooks/useVoiceSession';
import './VoiceCallInterface.css';

/**
 * MHI Assistant voice panel (mhi-voice protocol, see hooks/useVoiceSession.js).
 *
 * Docked, not modal: the 3D stage stays visible so voice commands and answers
 * can drive selection and highlights while the assistant speaks. Audio capture
 * is 16 kHz PCM from an AudioWorklet with the mic kept open for barge-in.
 *
 * Agent mode (CONTRACT 9.3, 10.5): mode, clientId, talkMode and uiFocus pass
 * through to useVoiceSession; onUiMode / onAgentState report "open agent mode"
 * and the voice confirmation state. With autoStart (?agent=1) the panel opens
 * without a click, so a browser that keeps the AudioContext suspended gets one
 * "Start voice" button: that click is the gesture audio needs.
 *
 * While open, the panel's height is published as --vcall-h on <html>, so agent mode's stage caption
 * can sit clear of the docked panel (StageCaption.css).
 *
 * Push to talk ("MHI, push to talk"): the mic is gated by useVoiceSession and opens only while the
 * "Hold to talk" button or the space bar is held (not while typing in a field).
 */

const STATE_LABEL = {
  idle: 'Not connected',
  connecting: 'Connecting',
  listening: 'Listening',
  user_speaking: 'Hearing you',
  thinking: 'Thinking',
  speaking: 'Speaking',
  error: 'Unavailable',
};

const ROLE_LABEL = { cause: 'Cause', replace: 'Replace', check: 'Check', affected: 'Affected', context: 'Context' };

/** 'R Main Gear' -> 'Right main gear'; acronyms stay ('VSD Inverter' -> 'VSD inverter'). */
const partLabel = (name) => {
  const keep = (w) => w.length > 1 && /[A-Z]/.test(w) && w === w.toUpperCase();
  const words = String(name || '').split(' ').map((w) => ({ R: 'right', L: 'left' }[w] ?? (keep(w) ? w : w.toLowerCase())));
  const s = words.join(' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const DEFAULT_HINT = 'Ask about a part, or say “open the right engine”, “deeper”, “explode 50 percent”.';
const AGENT_HINT = {
  wake: 'Say “MHI” and then what you need, for example “MHI, start the line replay”.',
  open: 'Talk to the specialist: “what are you doing?”, “open the terminal”, “approve the quote”.',
  ptt: 'Push to talk is on: hold the talk button or the space bar while you speak. Say “hands-free” to go back to the wake word.',
};
/** Per-asset opening hint and tap-to-ask prompts (the §9.6 demo questions for the vacuum pump). */
const ASSET_PROMPTS = {
  vacuum_pump: {
    hint: 'Ask about the pump, or say “show me the gas ballast”, “explode the oil circuit”, “trace the back-streaming fault”.',
    prompts: [
      'Is it the pump or my process?',
      'Why is there water in the oil?',
      'Show me the gas ballast',
      'Show the trace for the back-streaming fault',
      'Walk me through the rate of rise test',
      'Are these alarms related?',
    ],
  },
};

const isTyping = (el) => Boolean(el) && (
  el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable
);

const ms = (v) => (typeof v === 'number' ? (v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${v} ms`) : null);

const VoiceCallInterface = ({
  isOpen,
  onClose,
  wssEndpoint,
  prearmedAudio = null,
  asset,
  assetLabel,
  focusPartId = null,
  focusPartName = null,
  telemetry = null,
  onUiAction,
  mode = null,
  clientId = null,
  talkMode = null,
  uiFocus = null,
  onUiMode,
  onAgentState,
  autoStart = false,
}) => {
  const voice = useVoiceSession({
    url: wssEndpoint, asset, assetLabel, focusPartId, focusPartName, telemetry, onUiAction,
    mode, clientId, talkMode, uiFocus, onUiMode, onAgentState,
  });
  const { state, start, end, pttActive, setPttHeld } = voice;
  const agentMode = mode === 'agent';
  const [draft, setDraft] = useState('');
  const startedRef = useRef(false);
  const pttLive = isOpen && pttActive && state !== 'idle' && state !== 'error' && state !== 'connecting';

  // Push to talk: the space bar holds the mic open (outside text fields); losing focus releases it.
  useEffect(() => {
    if (!pttLive) return undefined;
    const down = (e) => {
      if (e.code !== 'Space' || e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target)) return;
      e.preventDefault();
      if (!e.repeat) setPttHeld(true);
    };
    const up = (e) => {
      if (e.code !== 'Space') return;
      if (!isTyping(e.target)) e.preventDefault();
      setPttHeld(false);
    };
    const release = () => setPttHeld(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', release);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', release);
      setPttHeld(false);
    };
  }, [pttLive, setPttHeld]);

  // Publish the panel height for the agent-mode stage caption; cleared when the panel closes.
  const panelRef = useRef(null);
  useEffect(() => {
    const el = panelRef.current;
    if (!isOpen || !el) return undefined;
    const root = document.documentElement;
    const update = () => root.style.setProperty('--vcall-h', `${Math.round(el.offsetHeight)}px`);
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      root.style.removeProperty('--vcall-h');
    };
  }, [isOpen]);

  // Start once per open, reusing the AudioContext created in the click that opened us.
  useEffect(() => {
    if (isOpen && !startedRef.current) {
      startedRef.current = true;
      start(prearmedAudio?.ctx ?? null);
    }
    if (!isOpen && startedRef.current) {
      startedRef.current = false;
      end();
    }
  }, [isOpen, prearmedAudio, start, end]);

  if (!isOpen) return null;

  const endCall = () => {
    startedRef.current = false;
    end();
    onClose?.();
  };

  const submit = (e) => {
    e.preventDefault();
    if (voice.sendText(draft)) setDraft('');
  };

  const m = voice.metrics;
  const metricParts = m ? [
    ['stt', ms(m.stt_ms)],
    ['retrieval', ms(m.retrieval_ms)],
    ['first token', ms(m.llm_ttft_ms)],
    ['tts', ms(m.tts_ttfb_ms)],
    ['first audio', ms(m.first_audio_ms)],
  ].filter(([, v]) => v) : [];

  const busy = state === 'connecting';
  const live = state !== 'idle' && state !== 'error' && state !== 'connecting';
  const needsGesture = autoStart && voice.audioState === 'suspended' && state !== 'idle' && state !== 'error';
  const hint = agentMode
    ? AGENT_HINT[voice.agentState?.talk_mode || talkMode || 'wake'] ?? AGENT_HINT.wake
    : ASSET_PROMPTS[asset]?.hint ?? DEFAULT_HINT;

  return (
    <section ref={panelRef} className={`vcall is-${state}${agentMode ? ' is-agent' : ''}`} aria-label="MHI Assistant voice">
      <header className="vcall__head">
        <div className="vcall__title">
          <span className="vcall__name">{agentMode ? 'MHI Assistant · agent mode' : 'MHI Assistant'}</span>
          <span className="vcall__asset">{assetLabel}{focusPartName ? ` · ${partLabel(focusPartName)}` : ''}</span>
        </div>
        <span className="vcall__state" role="status" aria-live="polite">
          <span className="vcall__dot" aria-hidden="true" />
          {voice.muted && live ? 'Muted'
            : pttActive && live && !voice.pttHeld && state === 'listening' ? 'Hold to talk'
              : STATE_LABEL[state] ?? state}
        </span>
      </header>

      <div className="vcall__body">
        {needsGesture && (
          <button type="button" className="vcall__start" onClick={voice.resumeAudio}>
            <Mic size={16} strokeWidth={1.8} aria-hidden="true" />
            <span>Start voice</span>
          </button>
        )}
        {!voice.userText && !voice.partial && !voice.answer && live && (
          <p className="vcall__hint">{hint}</p>
        )}
        {!agentMode && !voice.userText && !voice.partial && !voice.answer && live && ASSET_PROMPTS[asset]?.prompts && (
          <div className="vcall__parts" aria-label="Suggested questions">
            {ASSET_PROMPTS[asset].prompts.map((q) => (
              <button key={q} type="button" className="vcall__chip is-prompt" onClick={() => voice.sendText(q)}>
                {q}
              </button>
            ))}
          </div>
        )}
        {busy && <p className="vcall__hint">Opening the microphone and connecting…</p>}

        {(voice.partial || voice.userText) && (
          <div className="vcall__turn is-user">
            <span className="vcall__who">You</span>
            <p className={`vcall__text${voice.partial ? ' is-partial' : ''}`}>{voice.partial || voice.userText}</p>
          </div>
        )}

        {(voice.answer || state === 'thinking') && (
          <div className="vcall__turn is-bot">
            <span className="vcall__who">Assistant</span>
            <p className="vcall__text">{voice.answer || 'Thinking…'}</p>
          </div>
        )}

        {voice.evidence.parts.length > 0 && (
          <div className="vcall__parts" aria-label="Parts in this answer">
            {voice.evidence.parts.map((p) => (
              <button
                key={p.cad_id}
                type="button"
                className={`vcall__chip is-${p.role || 'context'}`}
                title={p.cad_id}
                onClick={() => onUiAction?.({ type: 'select', part_ids: [p.cad_id], t: 0.5 })}
              >
                <span className="vcall__chip-role">{ROLE_LABEL[p.role] ?? 'Part'}</span>
                {partLabel(p.name || p.cad_id)}
              </button>
            ))}
          </div>
        )}

        {voice.error && <p className="vcall__error" role="alert">{voice.error}</p>}
      </div>

      {metricParts.length > 0 && (
        <p className="vcall__metrics" title="Last turn latency">
          {metricParts.map(([k, v]) => `${k} ${v}`).join(' · ')}
        </p>
      )}

      <form className="vcall__ask" onSubmit={submit}>
        <input
          type="text"
          value={draft}
          maxLength={1000}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Or type a question"
          aria-label="Type a question"
          disabled={!live}
        />
        <button type="submit" className="vcall__icon" disabled={!live || !draft.trim()} aria-label="Send" title="Send">
          <Send size={15} strokeWidth={1.8} aria-hidden="true" />
        </button>
      </form>

      <footer className="vcall__controls">
        {pttActive && (
          <button
            type="button"
            className={`vcall__btn vcall__ptt${voice.pttHeld ? ' is-on' : ''}`}
            disabled={!live || voice.muted}
            aria-pressed={voice.pttHeld}
            title="Hold while you speak (or hold the space bar)"
            onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); setPttHeld(true); }}
            onPointerUp={() => setPttHeld(false)}
            onPointerCancel={() => setPttHeld(false)}
            onLostPointerCapture={() => setPttHeld(false)}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) { e.preventDefault(); e.stopPropagation(); setPttHeld(true); } }}
            onKeyUp={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setPttHeld(false); } }}
            onContextMenu={(e) => e.preventDefault()}
          >
            <Hand size={16} strokeWidth={1.8} aria-hidden="true" />
            <span>{voice.pttHeld ? 'Talking…' : 'Hold to talk'}</span>
          </button>
        )}
        <button
          type="button"
          className={`vcall__btn${voice.muted ? ' is-on' : ''}`}
          onClick={() => voice.setMuted(!voice.muted)}
          disabled={!live}
          aria-pressed={voice.muted}
        >
          {voice.muted ? <MicOff size={16} strokeWidth={1.8} aria-hidden="true" /> : <Mic size={16} strokeWidth={1.8} aria-hidden="true" />}
          <span>{voice.muted ? 'Unmute' : 'Mute'}</span>
        </button>
        {state === 'error' && (
          <button type="button" className="vcall__btn" onClick={() => start(null)}>Retry</button>
        )}
        <button type="button" className="vcall__btn is-end" onClick={endCall}>
          <PhoneOff size={16} strokeWidth={1.8} aria-hidden="true" />
          <span>End</span>
        </button>
      </footer>
    </section>
  );
};

export default VoiceCallInterface;
